import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Shield, Mail, Lock, User, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ROLES = [
  { value: 'ADMIN_ESTADUAL', label: 'Admin Estadual' },
  { value: 'CASA_CIVIL', label: 'Casa Civil' },
  { value: 'CGE', label: 'CGE' },
  { value: 'SECRETARIA_EXECUTORA', label: 'Secretaria Executora' },
  { value: 'UNIDADE_INTERNA', label: 'Unidade Interna' },
  { value: 'JURIDICO', label: 'Jurídico' },
  { value: 'GOVERNADOR', label: 'Governador' },
  { value: 'OBSERVADOR_EXTERNO', label: 'Observador Externo' },
];

const STATES = [
  { id: 'AC', name: 'Acre' },
  { id: 'AL', name: 'Alagoas' },
  { id: 'AP', name: 'Amapá' },
  { id: 'AM', name: 'Amazonas' },
  { id: 'BA', name: 'Bahia' },
  { id: 'CE', name: 'Ceará' },
  { id: 'DF', name: 'Distrito Federal' },
  { id: 'ES', name: 'Espírito Santo' },
  { id: 'GO', name: 'Goiás' },
  { id: 'MA', name: 'Maranhão' },
  { id: 'MT', name: 'Mato Grosso' },
  { id: 'MS', name: 'Mato Grosso do Sul' },
  { id: 'MG', name: 'Minas Gerais' },
  { id: 'PA', name: 'Pará' },
  { id: 'PB', name: 'Paraíba' },
  { id: 'PR', name: 'Paraná' },
  { id: 'PE', name: 'Pernambuco' },
  { id: 'PI', name: 'Piauí' },
  { id: 'RJ', name: 'Rio de Janeiro' },
  { id: 'RN', name: 'Rio Grande do Norte' },
  { id: 'RS', name: 'Rio Grande do Sul' },
  { id: 'RO', name: 'Rondônia' },
  { id: 'RR', name: 'Roraima' },
  { id: 'SC', name: 'Santa Catarina' },
  { id: 'SP', name: 'São Paulo' },
  { id: 'SE', name: 'Sergipe' },
  { id: 'TO', name: 'Tocantins' },
];

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [ssoTenantId, setSsoTenantId] = useState('SP');
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(false);

  // Aceite institucional dos termos legais (LGPD + Termos de Uso)
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Restaura aceite persistido (após primeiro consentimento)
  useEffect(() => {
    try {
      const saved = localStorage.getItem('bluegov_terms_acceptance');
      if (saved) {
        const data = JSON.parse(saved);
        if (data?.accepted_at) setAcceptedTerms(true);
      }
    } catch { /* ignore */ }
  }, []);

  const persistAcceptance = () => {
    try {
      localStorage.setItem('bluegov_terms_acceptance', JSON.stringify({
        accepted_at: new Date().toISOString(),
        versions: { termos: '1.0', privacidade: '1.0' },
      }));
    } catch { /* ignore */ }
  };

  // Check Azure AD SSO availability for tenant
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ssoTenantId) {
        if (!cancelled) setSsoEnabled(false);
        return;
      }
      if (!cancelled) setSsoChecking(true);
      const api = process.env.REACT_APP_BACKEND_URL || '';
      try {
        const r = await axios.get(`${api}/api/auth/azure-ad/status`, { params: { tenant_id: ssoTenantId } });
        if (!cancelled) setSsoEnabled(!!r.data?.enabled);
      } catch {
        if (!cancelled) setSsoEnabled(false);
      } finally {
        if (!cancelled) setSsoChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ssoTenantId]);

  // Show Azure AD callback error if any
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('azure_error');
    if (err) {
      toast.error(`SSO Microsoft: ${err}`);
      // limpa query
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleAzureLogin = () => {
    if (!acceptedTerms) {
      toast.error('Para continuar, é necessário aceitar os Termos de Uso e a Política de Privacidade.');
      return;
    }
    persistAcceptance();
    const api = process.env.REACT_APP_BACKEND_URL || '';
    const url = `${api}/api/auth/azure-ad/login?tenant_id=${encodeURIComponent(ssoTenantId)}&redirect_path=${encodeURIComponent('/dashboard')}`;
    window.location.href = url;
  };

  // Register form state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerRole, setRegisterRole] = useState('');
  const [registerTenant, setRegisterTenant] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error('Para continuar, é necessário aceitar os Termos de Uso e a Política de Privacidade.');
      return;
    }
    setLoading(true);
    try {
      const result = await login(loginEmail, loginPassword);
      if (result.success) {
        persistAcceptance();
        toast.success('Login realizado com sucesso!');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Erro ao fazer login');
      }
    } catch (error) {
      toast.error('Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error('Para criar a conta, é necessário aceitar os Termos de Uso e a Política de Privacidade.');
      return;
    }
    if (!registerRole || !registerTenant) {
      toast.error('Por favor, selecione o perfil e o estado');
      return;
    }
    setLoading(true);
    const result = await register({
      name: registerName,
      email: registerEmail,
      password: registerPassword,
      role: registerRole,
      tenant_id: registerTenant
    });
    setLoading(false);
    if (result.success) {
      persistAcceptance();
      toast.success('Conta criada com sucesso!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Erro ao criar conta');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" />
        {/* Decorative elements (atrás do conteúdo) */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-white/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 right-40 w-48 h-48 bg-white/5 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16 py-12 w-full">
          {/* Marca */}
          <div className="flex items-center gap-4 mb-10">
            <img
              src="/bluegov-logo.png"
              alt="BlueGov"
              className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur object-contain p-1"
            />
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">BlueGov</h1>
              <p className="text-white/60 text-xs uppercase tracking-[0.18em]">
                Plataforma de Conformidade e Proteção Institucional
              </p>
            </div>
          </div>

          {/* Título principal */}
          <h2
            className="text-4xl xl:text-5xl font-extrabold text-white leading-[1.1] tracking-tight mb-6"
            data-testid="login-headline"
          >
            Toda auditoria gera obrigações.
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-sky-200 to-cyan-200">
              O BlueGov garante que elas sejam cumpridas.
            </span>
          </h2>

          {/* Subtítulo */}
          <div className="space-y-3 max-w-lg mb-10" data-testid="login-subheadline">
            <p className="text-base xl:text-lg text-white/85 leading-relaxed">
              Centralize determinações, recomendações, auditorias, diligências e planos de ação em uma única plataforma.
            </p>
            <p className="text-sm xl:text-base text-white/70 leading-relaxed">
              Acompanhe prazos, organize evidências, demonstre conformidade e reduza riscos institucionais com total rastreabilidade.
            </p>
          </div>

          {/* Indicadores institucionais */}
          <div className="grid grid-cols-3 gap-3 max-w-lg" data-testid="login-stats">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10 flex flex-col">
              <p className="text-2xl xl:text-3xl font-extrabold text-white leading-none">IPI</p>
              <p className="text-[11px] text-white/70 leading-snug mt-2">
                Índice de Proteção Institucional
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10 flex flex-col">
              <p className="text-xl xl:text-2xl font-extrabold text-white leading-none">Hash Chain</p>
              <p className="text-[11px] text-white/70 leading-snug mt-2">
                Auditoria Imutável
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10 flex flex-col">
              <p className="text-xl xl:text-2xl font-extrabold text-white leading-none">Dossiê</p>
              <p className="text-[11px] text-white/70 leading-snug mt-2">
                Verificação Pública
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img 
              src="/bluegov-logo.png" 
              alt="BlueGov" 
              className="w-12 h-12 rounded-xl object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-slate-900">BlueGov</h1>
              <p className="text-xs text-slate-500">Plataforma de Conformidade e Proteção Institucional</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-slate-900">Bem-vindo</CardTitle>
              <CardDescription>Acesse sua conta ou crie uma nova</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Entrar</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Cadastrar</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">E-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="seu@email.gov.br"
                          className="pl-10"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-700 hover:bg-blue-800"
                      disabled={loading || !acceptedTerms}
                      data-testid="login-submit-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Entrar
                    </Button>

                    {/* SSO Microsoft Entra ID */}
                    <div className="relative my-2" data-testid="sso-divider">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                      <div className="relative flex justify-center"><span className="bg-white px-2 text-xs text-slate-500">ou autenticação corporativa</span></div>
                    </div>

                    <div className="space-y-2" data-testid="sso-azure-block">
                      <Label htmlFor="sso-tenant" className="text-xs text-slate-500">Estado / Tenant</Label>
                      <Select value={ssoTenantId} onValueChange={setSsoTenantId}>
                        <SelectTrigger id="sso-tenant" data-testid="sso-tenant-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATES.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.id} — {s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full border-slate-300"
                        onClick={handleAzureLogin}
                        disabled={!ssoEnabled || ssoChecking || !acceptedTerms}
                        data-testid="sso-microsoft-btn"
                      >
                        {ssoChecking ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 23 23" aria-hidden>
                            <rect width="10" height="10" x="1" y="1" fill="#F25022"/>
                            <rect width="10" height="10" x="12" y="1" fill="#7FBA00"/>
                            <rect width="10" height="10" x="1" y="12" fill="#00A4EF"/>
                            <rect width="10" height="10" x="12" y="12" fill="#FFB900"/>
                          </svg>
                        )}
                        Entrar com Microsoft
                      </Button>
                      {!ssoEnabled && !ssoChecking && (
                        <p className="text-[10px] text-slate-400 text-center" data-testid="sso-disabled-msg">
                          SSO Microsoft não configurado para este tenant. Solicite ao administrador.
                        </p>
                      )}
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nome Completo</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Seu nome"
                          className="pl-10"
                          value={registerName}
                          onChange={(e) => setRegisterName(e.target.value)}
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">E-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="seu@email.gov.br"
                          className="pl-10"
                          value={registerEmail}
                          onChange={(e) => setRegisterEmail(e.target.value)}
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          required
                          data-testid="register-password-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-tenant">Estado</Label>
                      <Select value={registerTenant} onValueChange={setRegisterTenant}>
                        <SelectTrigger data-testid="register-tenant-select">
                          <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                          <SelectValue placeholder="Selecione o estado" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATES.map((state) => (
                            <SelectItem key={state.id} value={state.id}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-role">Perfil</Label>
                      <Select value={registerRole} onValueChange={setRegisterRole}>
                        <SelectTrigger data-testid="register-role-select">
                          <Shield className="w-4 h-4 mr-2 text-slate-400" />
                          <SelectValue placeholder="Selecione o perfil" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-700 hover:bg-blue-800"
                      disabled={loading || !acceptedTerms}
                      data-testid="register-submit-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Criar Conta
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Aceite legal — LGPD + Termos de Uso */}
          <div
            className="mt-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
            data-testid="legal-consent-block"
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-terms"
                checked={acceptedTerms}
                onCheckedChange={(v) => setAcceptedTerms(v === true)}
                className="mt-0.5"
                data-testid="accept-terms-checkbox"
              />
              <label htmlFor="accept-terms" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                Declaro que li e concordo com os{' '}
                <a
                  href="/termos-de-uso"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                  data-testid="link-termos"
                >
                  Termos de Uso
                </a>{' '}
                e com a{' '}
                <a
                  href="/politica-de-privacidade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                  data-testid="link-privacidade"
                >
                  Política de Privacidade
                </a>
                , autorizando o tratamento dos meus dados pessoais nos termos da{' '}
                <a
                  href="/lgpd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                  data-testid="link-lgpd"
                >
                  LGPD
                </a>
                .
              </label>
            </div>
            {!acceptedTerms && (
              <p className="mt-2 ml-7 text-[11px] text-slate-400" data-testid="legal-consent-hint">
                É necessário aceitar para entrar ou criar conta.
              </p>
            )}
          </div>

          <p className="text-center text-[11px] text-slate-400 mt-4">
            © {new Date().getFullYear()} BlueGov · Plataforma de Conformidade e Proteção Institucional
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
